/**
 * PageSettingsPanel
 *
 * Right-side Sheet panel for page-level settings: SEO, Layout, Advanced.
 */
import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../ui/tabs';
import { Separator } from '../ui/separator';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface PageSettingsPanelSettings {
  title: string;
  slug: string;
  description?: string;
  ogImage?: string;
  layout: 'default' | 'data-room';
  maxWidth: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  spacing: 'compact' | 'normal' | 'relaxed';
  entityType?: 'offering' | 'property' | 'site' | null;
  entityId?: string;
  status: 'draft' | 'published';
}

export interface PageSettingsPanelProps {
  isOpen: boolean;
  settings: PageSettingsPanelSettings;
  onClose: () => void;
  onUpdate: (settings: Partial<PageSettingsPanelSettings>) => void;
  onDelete?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Google Search Preview                                              */
/* ------------------------------------------------------------------ */

function GooglePreview({ title, slug, description }: { title: string; slug: string; description?: string }) {
  const displayTitle = title ? (title.length > 60 ? title.slice(0, 57) + '…' : title) : 'Page Title';
  const displayUrl = `yourdomain.com/${slug || 'page-slug'}`;
  const displayDesc = description
    ? description.length > 160
      ? description.slice(0, 157) + '…'
      : description
    : 'Add a meta description to control how this page appears in search results.';

  return (
    <div className="rounded-lg border border-border bg-background p-4 space-y-1">
      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-2">
        Search Preview
      </p>
      <p className="text-blue-600 text-sm font-medium leading-tight truncate">
        {displayTitle}
      </p>
      <p className="text-green-700 text-xs leading-tight truncate">
        {displayUrl}
      </p>
      <p className="text-gray-600 text-xs leading-snug line-clamp-2">
        {displayDesc}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function PageSettingsPanel({
  isOpen,
  settings,
  onClose,
  onUpdate,
  onDelete,
}: PageSettingsPanelProps) {
  const [local, setLocal] = useState<PageSettingsPanelSettings>(settings);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [ogPreviewError, setOgPreviewError] = useState(false);

  // Sync local when settings prop changes
  useEffect(() => {
    setLocal(settings);
  }, [settings]);

  const update = <K extends keyof PageSettingsPanelSettings>(
    key: K,
    value: PageSettingsPanelSettings[K]
  ) => {
    const next = { ...local, [key]: value };
    setLocal(next);
    onUpdate({ [key]: value });
  };

  const descLength = local.description?.length ?? 0;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        side="right"
        className="w-[420px] sm:max-w-[420px] flex flex-col p-0 overflow-hidden"
      >
        {/* Header */}
        <SheetHeader className="px-5 py-4 border-b border-border shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-base font-semibold">Page Settings</SheetTitle>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </SheetHeader>

        {/* Tabs */}
        <div className="flex-1 overflow-y-auto">
          <Tabs defaultValue="seo" className="h-full">
            <div className="px-5 pt-4 border-b border-border shrink-0">
              <TabsList className="w-full">
                <TabsTrigger value="seo" className="flex-1">SEO</TabsTrigger>
                <TabsTrigger value="layout" className="flex-1">Layout</TabsTrigger>
                <TabsTrigger value="advanced" className="flex-1">Advanced</TabsTrigger>
              </TabsList>
            </div>

            {/* ── SEO Tab ─────────────────────────────────────────── */}
            <TabsContent value="seo" className="p-5 space-y-5 mt-0">
              {/* Page Title */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">
                  Page Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={local.title}
                  onChange={(e) => update('title', e.target.value)}
                  placeholder="My Page Title"
                  className="text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  Appears in browser tab and search results. Keep under 60 characters.
                </p>
              </div>

              <Separator />

              {/* URL Slug */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">
                  URL Slug <span className="text-destructive">*</span>
                </Label>
                <div className="flex items-center gap-0">
                  <span className="px-3 h-9 flex items-center bg-muted border border-r-0 border-border rounded-l-md text-sm text-muted-foreground shrink-0">
                    /
                  </span>
                  <Input
                    value={local.slug}
                    onChange={(e) =>
                      update(
                        'slug',
                        e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, '-')
                          .replace(/[^a-z0-9-]/g, '')
                      )
                    }
                    placeholder="page-slug"
                    className="rounded-l-none text-sm"
                  />
                </div>
              </div>

              <Separator />

              {/* Meta Description */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-muted-foreground">
                    Meta Description
                  </Label>
                  <span
                    className={`text-[11px] ${
                      descLength > 160 ? 'text-destructive' : 'text-muted-foreground'
                    }`}
                  >
                    {descLength}/160
                  </span>
                </div>
                <Textarea
                  value={local.description ?? ''}
                  onChange={(e) => update('description', e.target.value)}
                  placeholder="Brief description of this page for search engines..."
                  rows={3}
                  maxLength={200}
                  className="text-sm resize-none"
                />
              </div>

              <Separator />

              {/* OG Image */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">OG Image URL</Label>
                <Input
                  value={local.ogImage ?? ''}
                  onChange={(e) => {
                    update('ogImage', e.target.value);
                    setOgPreviewError(false);
                  }}
                  placeholder="https://..."
                  className="text-sm"
                />
                {local.ogImage && !ogPreviewError && (
                  <div className="rounded-md overflow-hidden border border-border h-28 bg-muted">
                    <img
                      src={local.ogImage}
                      alt="OG preview"
                      className="w-full h-full object-cover"
                      onError={() => setOgPreviewError(true)}
                    />
                  </div>
                )}
                {ogPreviewError && (
                  <p className="text-[11px] text-destructive">Could not load image preview.</p>
                )}
              </div>

              <Separator />

              {/* Search preview */}
              <GooglePreview
                title={local.title}
                slug={local.slug}
                description={local.description}
              />
            </TabsContent>

            {/* ── Layout Tab ──────────────────────────────────────── */}
            <TabsContent value="layout" className="p-5 space-y-5 mt-0">
              {/* Layout Mode */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Layout Mode</Label>
                <div className="grid grid-cols-2 gap-2">
                  {(
                    [
                      { value: 'default', label: 'Default', desc: 'Single column' },
                      { value: 'data-room', label: 'Data Room', desc: 'Sidebar + main' },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => update('layout', opt.value)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        local.layout === opt.value
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <p className="text-xs font-medium">{opt.label}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Max Width */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Max Width</Label>
                <Select
                  value={local.maxWidth}
                  onValueChange={(v) =>
                    update('maxWidth', v as PageSettingsPanelSettings['maxWidth'])
                  }
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sm">Small (48rem)</SelectItem>
                    <SelectItem value="md">Medium (64rem)</SelectItem>
                    <SelectItem value="lg">Large (72rem)</SelectItem>
                    <SelectItem value="xl">XL (80rem)</SelectItem>
                    <SelectItem value="full">Full Width</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Spacing */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Block Spacing</Label>
                <Select
                  value={local.spacing}
                  onValueChange={(v) =>
                    update('spacing', v as PageSettingsPanelSettings['spacing'])
                  }
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compact">Compact</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="relaxed">Relaxed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Entity Type */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Entity Type</Label>
                <Select
                  value={local.entityType ?? 'none'}
                  onValueChange={(v) =>
                    update(
                      'entityType',
                      v === 'none' ? null : (v as PageSettingsPanelSettings['entityType'])
                    )
                  }
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="offering">Offering</SelectItem>
                    <SelectItem value="property">Property</SelectItem>
                    <SelectItem value="site">Site</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Entity ID — shown only if entity type is set */}
              {local.entityType && local.entityType !== null && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">Entity ID</Label>
                  <Input
                    value={local.entityId ?? ''}
                    onChange={(e) => update('entityId', e.target.value)}
                    placeholder="Enter entity UUID..."
                    className="text-sm font-mono"
                  />
                </div>
              )}
            </TabsContent>

            {/* ── Advanced Tab ────────────────────────────────────── */}
            <TabsContent value="advanced" className="p-5 space-y-5 mt-0">
              {/* Status */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground">Page Status</Label>
                <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium">
                      {local.status === 'published' ? 'Published' : 'Draft'}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {local.status === 'published'
                        ? 'This page is live and publicly accessible.'
                        : 'This page is not yet visible to the public.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={local.status === 'published' ? 'default' : 'outline'}
                      className="text-[10px]"
                    >
                      {local.status === 'published' ? 'Live' : 'Draft'}
                    </Badge>
                    <Switch
                      checked={local.status === 'published'}
                      onCheckedChange={(checked) =>
                        update('status', checked ? 'published' : 'draft')
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Danger zone */}
              {onDelete && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-destructive uppercase tracking-wider">
                    Danger Zone
                  </p>
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-destructive">Delete this page</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          This action cannot be undone. All blocks and settings will be
                          permanently removed.
                        </p>
                      </div>
                    </div>
                    {!deleteConfirm ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeleteConfirm(true)}
                        className="w-full"
                      >
                        Delete Page
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs text-center text-destructive font-medium">
                          Are you sure? This is permanent.
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteConfirm(false)}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              setDeleteConfirm(false);
                              onDelete();
                            }}
                            className="flex-1"
                          >
                            Yes, delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}
